import { useAuth } from '../context/AuthContext';
import { useIsAdmin, usePermission } from '../hooks/usePermission';
import { PERMISSIONS } from '../constants/permissions';
import { useDashboardAccess } from '../components/dashboard/useDashboardAccess';

/**
 * Which home Dashboard.tsx draws for the signed-in person. ONE value per home branch, in the order the
 * branches are checked. Dashboard.tsx routes on it and DashboardStyleContext derives the Tiles/Thread
 * switch from it, so the two cannot drift.
 *  - board: the whole-business board (admins and the dashboard-view roles)
 *  - store: store staff and inventory heads
 *  - commercial: commercial managers (LC / export figures)
 *  - sales: sales heads and managers (the team board)
 *  - salesRep: sales reps (quotations, sales orders)
 *  - maintenance / technician: maintenance managers and technicians
 *  - merch: merchandisers (also a seller who holds the merchandising report)
 *  - people: HR managers (USER_MANAGE + DASHBOARD_VIEW + the People board); Tiles is the People dashboard page
 *  - personal: the UserDashboardView fallback
 */
export type DashboardHomeGroup =
    'board' | 'store' | 'commercial' | 'sales' | 'salesRep' | 'maintenance' | 'technician' | 'merch' | 'people' | 'personal';

/** 'systemAdmin' is the platform landing page; it has no group and no switch. */
export type DashboardHome = DashboardHomeGroup | 'systemAdmin';

/**
 * THE place that decides which homes have a Tiles/Thread switch. Enabling a home later is one line here
 * plus its Thread lens (client: the Thread branch for it in Dashboard.tsx; server: a ThreadLensStrategy).
 */
export const THREAD_ENABLED_GROUPS: ReadonlySet<DashboardHomeGroup> =
    new Set<DashboardHomeGroup>(['board', 'merch', 'sales', 'store', 'commercial', 'salesRep', 'maintenance', 'technician', 'people', 'personal']);

export const isThreadEnabled = (home: DashboardHome): home is DashboardHomeGroup =>
    home !== 'systemAdmin' && THREAD_ENABLED_GROUPS.has(home);

/**
 * Who may be OFFERED the Tiles/Thread switch for their home: the home has a Thread lens AND the caller holds the key that
 * lens's server strategy requires (else Thread would answer 403). The routed homes already imply their lens key; the
 * personal home does not (anyone falls through to it), so it needs MENU_DASHBOARD, the personal lens's requiredKey.
 */
export const isThreadEligible = (home: DashboardHome, has: (key: string) => boolean): home is DashboardHomeGroup =>
    isThreadEnabled(home) && (home !== 'personal' || has(PERMISSIONS.MENU_DASHBOARD));

/** The pure routing rule. `has` answers "does the caller hold this key in the selected institution". */
export function resolveDashboardHome(input: {
    systemAdminRole: boolean; isAdmin: boolean; has: (key: string) => boolean;
    /** The People module board is reachable (module on + a widget held): the same gate as roleLanding's boardGate('people'). */
    peopleBoardVisible?: boolean;
}): DashboardHome {
    const { systemAdminRole, isAdmin, has, peopleBoardVisible = false } = input;
    const P = PERMISSIONS;
    if (systemAdminRole) return 'systemAdmin';

    if (isAdmin || has(P.DASHBOARD_VIEW_MANAGER) || has(P.DASHBOARD_VIEW_ACCOUNTANT) || has(P.DASHBOARD_VIEW_PAYABLES)
        || has(P.DASHBOARD_VIEW_RECEIVABLES) || has(P.DASHBOARD_VIEW_PAYMENT_APPROVAL) || has(P.DASHBOARD_VIEW_AUDIT)) return 'board';
    if (has(P.DASHBOARD_INVENTORY_VIEW) || has(P.REQUISITION_ISSUE) || has(P.STOCK_ISSUE)) return 'store';
    if (has(P.COMMERCIAL_REPORT_VIEW) || has(P.LC_MANAGE)) return 'commercial';
    if (has(P.SALES_TEAM_BOARD_VIEW)) return 'sales';
    // A merchandiser who also holds the sales rep keys still counts as merch when they hold the merchandising
    // report, i.e. when their login lands on the Merchandising Dashboard.
    if (has(P.QUOTATION_VIEW) || has(P.SALES_ORDER_MANAGE)) {
        return has(P.BUYER_ORDER_VIEW) && has(P.MERCH_REPORT_VIEW) ? 'merch' : 'salesRep';
    }
    if (has(P.MAINTENANCE_WO_MANAGE) && has(P.MAINTENANCE_WO_VIEW_ALL)) return 'maintenance';
    if (has(P.MAINTENANCE_WO_WORK)) return 'technician';
    if (has(P.BUYER_ORDER_VIEW)) return 'merch';
    // HR manager: placed after every other home so nobody's existing routing changes.
    if (has(P.USER_MANAGE) && has(P.DASHBOARD_VIEW) && peopleBoardVisible) return 'people';
    return 'personal';
}

/** The home plus whether the caller may switch its style. Permission reads are explicit (fixed order). */
export function useDashboardHomeInfo(): { home: DashboardHome; threadEligible: boolean } {
    const { user } = useAuth();
    const isAdmin = useIsAdmin();
    const P = PERMISSIONS;
    const held = new Set<string>();
    const reads: Array<[string, boolean]> = [
        [P.DASHBOARD_VIEW_MANAGER, usePermission(P.DASHBOARD_VIEW_MANAGER)],
        [P.DASHBOARD_VIEW_ACCOUNTANT, usePermission(P.DASHBOARD_VIEW_ACCOUNTANT)],
        [P.DASHBOARD_VIEW_PAYABLES, usePermission(P.DASHBOARD_VIEW_PAYABLES)],
        [P.DASHBOARD_VIEW_RECEIVABLES, usePermission(P.DASHBOARD_VIEW_RECEIVABLES)],
        [P.DASHBOARD_VIEW_PAYMENT_APPROVAL, usePermission(P.DASHBOARD_VIEW_PAYMENT_APPROVAL)],
        [P.DASHBOARD_VIEW_AUDIT, usePermission(P.DASHBOARD_VIEW_AUDIT)],
        [P.DASHBOARD_INVENTORY_VIEW, usePermission(P.DASHBOARD_INVENTORY_VIEW)],
        [P.REQUISITION_ISSUE, usePermission(P.REQUISITION_ISSUE)],
        [P.STOCK_ISSUE, usePermission(P.STOCK_ISSUE)],
        [P.COMMERCIAL_REPORT_VIEW, usePermission(P.COMMERCIAL_REPORT_VIEW)],
        [P.LC_MANAGE, usePermission(P.LC_MANAGE)],
        [P.SALES_TEAM_BOARD_VIEW, usePermission(P.SALES_TEAM_BOARD_VIEW)],
        [P.QUOTATION_VIEW, usePermission(P.QUOTATION_VIEW)],
        [P.SALES_ORDER_MANAGE, usePermission(P.SALES_ORDER_MANAGE)],
        [P.BUYER_ORDER_VIEW, usePermission(P.BUYER_ORDER_VIEW)],
        [P.MERCH_REPORT_VIEW, usePermission(P.MERCH_REPORT_VIEW)],
        [P.MAINTENANCE_WO_MANAGE, usePermission(P.MAINTENANCE_WO_MANAGE)],
        [P.MAINTENANCE_WO_VIEW_ALL, usePermission(P.MAINTENANCE_WO_VIEW_ALL)],
        [P.MAINTENANCE_WO_WORK, usePermission(P.MAINTENANCE_WO_WORK)],
        [P.MENU_DASHBOARD, usePermission(P.MENU_DASHBOARD)],
        [P.USER_MANAGE, usePermission(P.USER_MANAGE)],
        [P.DASHBOARD_VIEW, usePermission(P.DASHBOARD_VIEW)],
    ];
    const { visibleModules } = useDashboardAccess();
    reads.forEach(([k, v]) => { if (v) held.add(k); });
    // SystemAdmin's own landing is platform-wide and reads the JWT role directly, not an institution permission set.
    const roles = user && user.roles ? (Array.isArray(user.roles) ? user.roles : [user.roles]) : [];
    const has = (k: string) => held.has(k);
    const home = resolveDashboardHome({
        systemAdminRole: roles.includes('SystemAdmin'), isAdmin, has, peopleBoardVisible: (visibleModules as Set<string>).has('people'),
    });
    return { home, threadEligible: isThreadEligible(home, has) };
}

/** The hook Dashboard.tsx calls. */
export function useDashboardHome(): DashboardHome {
    return useDashboardHomeInfo().home;
}
